import { useState } from "react";
import type { FormEvent } from "react";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import { API_BASE } from "@/config/api";
import { useSiteData } from "@/context/SiteDataContext";
import { mergeContactFormConfig } from "@/lib/contactFormConfig";
import { useT } from "@/lib/useSiteText";

export default function Contacto() {
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const siteSettings = data.siteSettings;
  const hero = data.pages.contacto;
  const cfg = mergeContactFormConfig(data.contentBlocks.contacto?.form_config?.[0]);
  const campos = cfg.campos;
  const [estado, setEstado] = useState<"idle" | "enviando" | "exito" | "error">("idle");
  const [mensajeError, setMensajeError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    setEstado("enviando");
    setMensajeError("");

    const payload: Record<string, string> = {};
    data.forEach((value, key) => {
      payload[key] = String(value);
    });

    try {
      const res = await fetch(`${API_BASE}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const parsed = await res.json().catch(() => null);

      if (res.ok && parsed?.ok) {
        setEstado("exito");
        form.reset();
      } else {
        setMensajeError(parsed?.error || "No pudimos enviar tu mensaje. Inténtalo nuevamente.");
        setEstado("error");
      }
    } catch {
      setMensajeError("Ocurrió un error de conexión. Inténtalo nuevamente.");
      setEstado("error");
    }
  }

  const inputCls =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

  return (
    <Layout>
      <PageHero
        titulo={hero.hero_titulo}
        descripcion={hero.hero_descripcion}
        imagen={hero.hero_imagen_url}
        alt={hero.hero_imagen_alt}
      />

      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              {cfg.lado_etiqueta && <span className="text-sm font-semibold text-accent-600">{cfg.lado_etiqueta}</span>}
              <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                {cfg.lado_titulo}
              </h2>
              {cfg.lado_descripcion && (
                <p className="mt-4 text-foreground-600 leading-relaxed">{cfg.lado_descripcion}</p>
              )}

              <div className="mt-8 space-y-5">
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 rounded-lg bg-background-100 p-4 hover:bg-background-200 transition"
                >
                  <span className="w-11 h-11 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-xl" />
                  </span>
                  <div>
                    <p className="font-heading font-semibold text-foreground-900 text-sm">{t("contacto.card_whatsapp")}</p>
                    <p className="text-sm text-foreground-600">{siteSettings.phone_display}</p>
                  </div>
                </a>
                <a
                  href={`tel:${siteSettings.phone_raw}`}
                  className="flex items-center gap-4 rounded-lg bg-background-100 p-4 hover:bg-background-200 transition"
                >
                  <span className="w-11 h-11 rounded-lg bg-secondary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-phone-line text-xl" />
                  </span>
                  <div>
                    <p className="font-heading font-semibold text-foreground-900 text-sm">{t("contacto.card_telefono")}</p>
                    <p className="text-sm text-foreground-600">{siteSettings.phone_display}</p>
                  </div>
                </a>
                <a
                  href={`mailto:${siteSettings.email}`}
                  className="flex items-center gap-4 rounded-lg bg-background-100 p-4 hover:bg-background-200 transition"
                >
                  <span className="w-11 h-11 rounded-lg bg-accent-500 text-background-50 flex items-center justify-center">
                    <i className="ri-mail-line text-xl" />
                  </span>
                  <div>
                    <p className="font-heading font-semibold text-foreground-900 text-sm">{t("contacto.card_email")}</p>
                    <p className="text-sm text-foreground-600">{siteSettings.email}</p>
                  </div>
                </a>
              </div>

              <div className="mt-8 rounded-lg bg-primary-900 p-6">
                <h3 className="font-heading font-semibold text-background-50">{t("comun.horario.titulo")}</h3>
                <ul className="mt-3 space-y-2 text-sm text-background-200">
                  <li className="flex justify-between">
                    <span>{t("comun.horario.lunes_viernes")}</span>
                    <span>{siteSettings.hours_weekday}</span>
                  </li>
                  <li className="flex justify-between">
                    <span>{t("comun.horario.sabado")}</span>
                    <span>{siteSettings.hours_saturday}</span>
                  </li>
                  <li className="flex justify-between">
                    <span>{t("comun.horario.acceso")}</span>
                    <span>{siteSettings.hours_units}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  {cfg.titulo}
                </h2>
                {cfg.subtitulo && <p className="mt-2 text-sm text-foreground-600">{cfg.subtitulo}</p>}

                {estado === "exito" && (
                  <div className="mt-5 rounded-md bg-primary-100 border border-primary-200 p-4 text-sm text-primary-800">
                    {cfg.mensaje_exito}
                  </div>
                )}
                {estado === "error" && (
                  <div className="mt-5 rounded-md bg-accent-100 border border-accent-200 p-4 text-sm text-accent-800">
                    {mensajeError}
                  </div>
                )}

                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  <div className="contact-auxiliary-field" aria-hidden="true">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <label htmlFor="nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {campos.nombre.label} *
                    </label>
                    <input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      placeholder={campos.nombre.placeholder}
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {campos.email.label} *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder={campos.email.placeholder}
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label htmlFor="telefono" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {campos.telefono.label} *
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      required
                      placeholder={campos.telefono.placeholder}
                      className={inputCls}
                    />
                  </div>
                  {campos.tipo_unidad.visible && (
                    <div className="sm:col-span-1">
                      <label htmlFor="tipo_unidad" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {campos.tipo_unidad.label}
                        {campos.tipo_unidad.required && " *"}
                      </label>
                      <select
                        id="tipo_unidad"
                        name="tipo_unidad"
                        required={campos.tipo_unidad.required}
                        className={inputCls}
                        defaultValue=""
                      >
                        <option value="" disabled={campos.tipo_unidad.required}>
                          {campos.tipo_unidad.placeholder}
                        </option>
                        {campos.tipo_unidad.opciones.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {campos.m2.visible && (
                    <div className="sm:col-span-1">
                      <label htmlFor="m2" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {campos.m2.label}
                        {campos.m2.required && " *"}
                      </label>
                      <select id="m2" name="m2" required={campos.m2.required} className={inputCls} defaultValue="">
                        <option value="" disabled={campos.m2.required}>
                          {campos.m2.placeholder}
                        </option>
                        {campos.m2.opciones.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {campos.fecha_inicio.visible && (
                    <div className="sm:col-span-1">
                      <label htmlFor="fecha_inicio" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {campos.fecha_inicio.label}
                        {campos.fecha_inicio.required && " *"}
                      </label>
                      <input
                        id="fecha_inicio"
                        name="fecha_inicio"
                        type="date"
                        required={campos.fecha_inicio.required}
                        className={inputCls}
                      />
                    </div>
                  )}
                  {campos.mensaje.visible && (
                    <div className="sm:col-span-2">
                      <label htmlFor="mensaje" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {campos.mensaje.label}
                        {campos.mensaje.required && " *"}
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        rows={4}
                        maxLength={campos.mensaje.max_caracteres}
                        required={campos.mensaje.required}
                        placeholder={campos.mensaje.placeholder}
                        className={`${inputCls} resize-none`}
                      />
                      <p className="mt-1 text-xs text-foreground-400">Máximo {campos.mensaje.max_caracteres} caracteres.</p>
                    </div>
                  )}

                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={estado === "enviando"}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {estado === "enviando" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" /> Enviando...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" /> {cfg.boton_enviar}
                        </>
                      )}
                    </button>
                    {cfg.aviso_legal && <p className="mt-3 text-xs text-foreground-500">{cfg.aviso_legal}</p>}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}